/**
 * Assistant-reply streaming — the ADR-081 D2 contract (proto
 * agnivo/gateway/v1 ReplyStream*): request-scoped SSE on
 * `POST /v1/messages:stream` (same request shape as SendMessage), event order
 * header → delta* → (final | error), then the stream CLOSES. Pre-stream
 * refusals are HTTP status (thrown as ApiError), not SSE events. No
 * mid-stream resume at go-live: end-of-stream without final/error is an
 * abnormal drop (surfaced as a synthetic CLIENT_STREAM_DROPPED error event) —
 * consumers re-fetch history (ListMessages) WITH RETRY (the assistant persist
 * lands after the delta phase). `@agnivo/api-client/mock` mirrors the framing.
 *
 * TWO WIRE SHAPES EXIST and this module parses the browser-facing one. The
 * agent-core → gateway hop is `POST /v1/client-intake`, which emits UNNAMED,
 * data-only SSE frames — `{delta, finishReason}`, terminal
 * `{done, message_id, full_text, finishReason, conversationId}`, terminal
 * `{error: {code, message}}`, with snake_case and camelCase mixed in the same
 * payload — and the gateway re-frames those into the named events below.
 *
 * Cite `gateway-service internal/adapter/client/agentcore/replystream.go:17-37`
 * for that contract, NOT agent-core's `src/http/server.ts`: that file serves two
 * routes, and the other one (`/v1/intake`, a different unauthenticated surface
 * being removed by agent-core!598) is not this hop. The gateway-side doc states
 * the route and all three frame shapes together (architect, 2026-07-27).
 *
 * Recorded because a `message_id`/`done` payload turns up in traces and is a REAL
 * wire, just not this one; reading it as a mismatch against this parser sends you
 * after a contract bug that isn't there.
 */
/** Absent or unknown values (incl. future enum members) collapse to UNSPECIFIED, which renders nothing. */
export function normalizeServingRoute(v) {
    return v === 'SERVING_ROUTE_OWNER_LOCAL' || v === 'SERVING_ROUTE_PLATFORM_CLOUD'
        ? v
        : 'SERVING_ROUTE_UNSPECIFIED';
}
/**
 * Incremental SSE parser, transport-agnostic and safe against frames split
 * across network chunks. Feed decoded text chunks; complete frames come back.
 * (The parser ships now so the SSE endpoint wiring is a thin layer later.)
 */
export function createSseParser() {
    let buffer = '';
    return {
        push(chunk) {
            buffer += chunk.replace(/\r\n/g, '\n');
            const frames = [];
            let sep;
            while ((sep = buffer.indexOf('\n\n')) !== -1) {
                const raw = buffer.slice(0, sep);
                buffer = buffer.slice(sep + 2);
                let event = 'message';
                let id;
                const dataLines = [];
                for (const line of raw.split('\n')) {
                    if (line.startsWith(':'))
                        continue; // comment
                    const colon = line.indexOf(':');
                    const field = colon === -1 ? line : line.slice(0, colon);
                    let value = colon === -1 ? '' : line.slice(colon + 1);
                    if (value.startsWith(' '))
                        value = value.slice(1);
                    if (field === 'event')
                        event = value;
                    else if (field === 'data')
                        dataLines.push(value);
                    else if (field === 'id')
                        id = value;
                }
                if (dataLines.length > 0 || event !== 'message') {
                    frames.push({ event, data: dataLines.join('\n'), id });
                }
            }
            return frames;
        },
    };
}
/**
 * SSE `event:` names for the reply stream — the cross-repo wire contract with the
 * gateway. VERIFIED IDENTICAL 2026-07-27 against `gateway-service` @ origin/dev,
 * `internal/adapter/grpc/replystream.go:43-52`, which declares the same four and
 * names THIS constant as the thing it honours.
 *
 * This comment used to say "PENDING PIN … proposed lowercase shorthand". The pin
 * happened on their side and nobody updated ours, so it read as an open question
 * long after it was settled — worse than no caveat, because a reader cannot tell
 * "checked and fine" from "nobody looked".
 *
 * WHAT ENFORCES IT, checked on both sides rather than assumed:
 *
 *   OURS — all four literals. `test/sse-live.test.ts` feeds raw
 *   `event: header|delta|final|error` frames and asserts the mapped output, so
 *   editing this table turns those tests red. Verified by MUTATION, not by
 *   reading: renaming `delta` here fails sse-live.test.ts:36.
 *
 *   THEIRS — three of the four. `replystream_test.go:471-479` is a byte-level
 *   golden spelling `event: header` / `event: delta` / `event: final` as literals
 *   in stream order; renaming any of those three fails their suite.
 *
 *   THE EXCEPTION IS `error`, and it is the one worth knowing about. No gateway
 *   test spells it literally — the error-path assertions all go through `dataOf`
 *   (replystream_test.go:539-545), which compares against their own
 *   `sseEventError` CONSTANT, so renaming it moves emitter and expectation
 *   together and their suite stays green. Ours stays green too (we test our
 *   parser against our own literals). The two would then disagree with nothing
 *   anywhere to say so.
 *
 * What that would cost: an unrecognised event maps to null, so no terminal event
 * arrives and the `!terminal` guard yields CLIENT_STREAM_DROPPED ("stream ended
 * before final"). Every typed refusal — including the ADR-092 agent-not-available
 * copy this file goes to some length to carry — would reach the user as a generic
 * drop instead. Loud enough to notice, wrong enough to misdiagnose.
 *
 * `default: return null` in mapFrame is deliberate forward-compat (the gateway
 * ADDING an event must not break older clients) and is also what makes the above
 * quiet rather than fatal. Trading it for a hard error is a real question with a
 * real cost, not an oversight — tracked in the fleet backlog, not decided here.
 */
export const REPLY_STREAM_EVENT_NAMES = {
    header: 'header',
    delta: 'delta',
    final: 'final',
    error: 'error',
};
/**
 * Live ReplyStream: fetch-based SSE POST to `/v1/messages:stream`
 * (Authorization header — native EventSource is GET-only and cannot set
 * headers; ADR-081 D2). `body` is the SendMessage request shape.
 *
 * ⛔ **STAYS fetch-based. There is now a SECOND reason, and it fails silently.**
 * ADR-081 D2 is the one above: EventSource cannot send `Authorization`, which
 * breaks loudly and immediately. The quiet one is the entitlement-degraded
 * payload (reason, plus tier / remaining / window_reset_at / upgrade_target in
 * `ErrorInfo.metadata`) riding `google.rpc.Status.details[]`, on exactly the
 * pre-stream refusal body this function reads with `res.json()` below.
 * EventSource surfaces a non-2xx as an opaque error event with **no body access
 * at all**, so a swap would silently regress the degraded UX to "something went
 * wrong".
 *
 * ⭐ **Two tests DO red on that, and this comment said otherwise until it was
 * measured.** It read "a swap would not fail a build or a test". The build half
 * is true — nothing type-checks differently. The test half was not: applying the
 * swap's observable consequence to the block below (keep `res.status`, drop the
 * `res.json()` read) reds exactly `test/sse-live.test.ts` "throws ApiError for a
 * pre-stream refusal" (asserts the SERVER's message, not the HTTP-status one)
 * and "carries Status details (google.rpc.ErrorInfo) through a pre-stream
 * refusal" (asserts `details[]` survives). Measured 2026-08-16 against
 * `origin/dev` `5b3f4be`: baseline 87/87 green, mutant 2 red, nothing else moved.
 *
 * So those two are the GUARD, not documentation — deleting or loosening either
 * removes the only mechanical objection to this swap and leaves prose. What they
 * do NOT cover is the D2 half (no `Authorization` on a GET-only transport); that
 * one breaks loudly at the first authenticated call, which is why it never
 * needed a test.
 *
 * So an "EventSource simplification" here is not a refactor with a caveat; it
 * deletes a product surface. BOTH sources pin it in the same words — a future
 * EventSource simplification "would silently lose the body":
 *   - architect R3 ruling, 2026-08-16T09:24Z (operative):
 *     `decisions/architect/2026-08-16-adr113-wire-contract-ratification.md`
 *   - `ADR-113 A2` ("D3's degraded shape on the wire", extends D3).
 *     ✅ A2 landed in the docs repo by amendment `40d3727` — re-verified 2026-09-01.
 *     This read "not yet on `dev` when this was written", which was true at the
 *     time and had been false since; a citation qualified by "not yet" rots the
 *     moment it lands, and reads as a live caveat forever.
 *     ⚠️ That sentence is worded to ARM `cross-repo-cites`, which is a real
 *     constraint and not style: the checker treats a line as a claim only when
 *     that ONE line carries both a repo token and one of its `ASSERTION_PHRASES`
 *     (`isCrossRepoClaim`, per-line; the pin may sit anywhere in the same
 *     block). The previous wording — "landed by amendment in `40d3727`" — had
 *     neither, so this comment was **never checked**, and a first attempt at
 *     this fix put `docs` and "landed in" on adjacent lines, which is also not
 *     checked. Measured both ways 2026-09-01: corrupting the sha below reds the
 *     gate now, and red nothing under either earlier wording.
 *     docs@e94c7ac2ca25:docs/architecture/adr/113-rag-product-surface-citations-confidence-attribution-followup.md:A2 (observed 2026-09-01)
 *     — verified by CONTENT on `origin/dev`, not by `--is-ancestor`: this repo
 *     squash-merges, so the amendment SHA is not an ancestor of anything and an
 *     ancestry check answers "no" for a merged change.
 * Cite A2, not D3 — D3 alone specifies no wire shape.
 *
 * Non-2xx BEFORE the stream (the pre-stream refusal class) throws ApiError on
 * the first iteration. End-of-stream without final/error emits the synthetic
 * CLIENT_STREAM_DROPPED error event.
 */
export function createSseReplyStream(cfg, body) {
    const controller = new AbortController();
    const fetchImpl = cfg.fetchImpl ?? fetch;
    async function* generate() {
        const headers = {
            ...(cfg.extraHeaders?.() ?? {}), // extras behind the reserved headers below
            Accept: 'text/event-stream',
            'Content-Type': 'application/json',
        };
        const token = cfg.getAccessToken ? await cfg.getAccessToken() : null;
        if (token)
            headers['Authorization'] = `Bearer ${token}`;
        let res;
        try {
            res = await fetchImpl(cfg.baseUrl.replace(/\/+$/, '') + '/v1/messages:stream', {
                method: 'POST',
                headers,
                body: JSON.stringify(body),
                signal: controller.signal,
            });
        }
        catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError')
                return;
            yield { kind: 'error', message: err instanceof Error ? err.message : 'network error', code: 'CLIENT_STREAM_DROPPED' };
            return;
        }
        if (!res.ok) {
            // pre-stream refusal: HTTP status, not an SSE event (contract) — throw
            // so callers handle it like any unary ApiError. `details` carries any
            // errorInfo (google.rpc.ErrorInfo), and per the 2026-08-16 R3 ruling /
            // ADR-113 A2 that is where the entitlement-degraded payload rides (tier,
            // remaining, window_reset_at, upgrade_target, inside `ErrorInfo.metadata`
            // — one details entry, "no second detail type in v1"). Nothing extra is needed
            // here — `details` was already captured verbatim; the change that made
            // that payload REACHABLE was teaching `ApiError.errorInfo` to carry
            // `metadata`, which it dropped until now. NOTE: ADR-092 AGENT_NOT_AVAILABLE does
            // NOT arrive here — the gateway writes the SSE header before calling
            // agent-core, so a refusal can only surface as the IN-STREAM error event
            // (code REPLY_ERROR_CODE_AGENT_NOT_AVAILABLE + agentNotAvailableReason);
            // see mapFrame + the ReplyEvent 'error' kind (architect ruling 2026-07-19,
            // gateway!160). This branch stays for other pre-stream HTTP refusals.
            const { ApiError, httpStatusMessage } = await import('./errors');
            let msg = httpStatusMessage(res.status);
            let grpcCode;
            let details;
            try {
                const status = (await res.json());
                if (status.message)
                    msg = status.message;
                grpcCode = status.code;
                details = status.details;
            }
            catch {
                // non-JSON body — keep the HTTP-status message
            }
            throw new ApiError(msg, res.status, grpcCode, details);
        }
        if (!res.body) {
            yield { kind: 'error', message: 'no response body', code: 'CLIENT_STREAM_DROPPED' };
            return;
        }
        const parser = createSseParser();
        const decoder = new TextDecoder();
        const reader = res.body.getReader();
        let terminal = false;
        try {
            for (;;) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
                    const ev = mapFrame(frame);
                    if (ev) {
                        if (ev.kind === 'final' || ev.kind === 'error')
                            terminal = true;
                        yield ev;
                        if (terminal)
                            return; // contract: nothing follows final/error
                    }
                }
            }
        }
        catch (err) {
            if (!(err instanceof DOMException && err.name === 'AbortError')) {
                yield { kind: 'error', message: err instanceof Error ? err.message : 'stream read failed', code: 'CLIENT_STREAM_DROPPED' };
            }
            return;
        }
        finally {
            reader.releaseLock();
        }
        if (!terminal) {
            // abnormal drop: the contract guarantees final|error before close
            yield { kind: 'error', message: 'stream ended before final', code: 'CLIENT_STREAM_DROPPED' };
        }
    }
    function mapFrame(frame) {
        let data;
        try {
            data = frame.data ? JSON.parse(frame.data) : {};
        }
        catch {
            return { kind: 'error', message: `unparseable ${frame.event} frame`, code: 'CLIENT_STREAM_DROPPED' };
        }
        switch (frame.event) {
            case REPLY_STREAM_EVENT_NAMES.header: {
                const h = data;
                return {
                    kind: 'header',
                    conversationId: h.conversationId ?? '',
                    inReplyToMessageId: h.inReplyToMessageId ?? '',
                };
            }
            case REPLY_STREAM_EVENT_NAMES.delta: {
                const d = data;
                return { kind: 'delta', text: d.delta ?? '' };
            }
            case REPLY_STREAM_EVENT_NAMES.final: {
                const f = data;
                return {
                    kind: 'final',
                    messageId: f.messageId ?? '',
                    fullText: f.fullText ?? '',
                    finishReason: f.finishReason,
                    servingRoute: normalizeServingRoute(f.servingRoute),
                };
            }
            case REPLY_STREAM_EVENT_NAMES.error: {
                const e = data;
                return {
                    kind: 'error',
                    message: e.message ?? 'reply failed',
                    code: e.code,
                    // Carried raw; the consumer validates against the known reason set.
                    ...(e.agentNotAvailableReason ? { agentNotAvailableReason: e.agentNotAvailableReason } : {}),
                };
            }
            default:
                return null; // unknown event names are ignored (forward-compat)
        }
    }
    const iterator = generate();
    return {
        [Symbol.asyncIterator]: () => iterator,
        close: () => {
            controller.abort();
            void iterator.return?.(undefined);
        },
    };
}
/**
 * Reads a grpc-gateway server-stream response (NEWLINE-DELIMITED JSON chunks,
 * each `{"result": <message>}` or `{"error": <status>}`) — used by
 * `GenerateFromDescription` (`POST /v1/agents:generateFromDescription`).
 * NOT SSE; the chat reply stream is the only SSE route (contract note).
 */
export async function* readNdjsonStream(res) {
    if (!res.body)
        return;
    const decoder = new TextDecoder();
    const reader = res.body.getReader();
    let buffer = '';
    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            let nl;
            while ((nl = buffer.indexOf('\n')) !== -1) {
                const line = buffer.slice(0, nl).trim();
                buffer = buffer.slice(nl + 1);
                if (!line)
                    continue;
                const parsed = JSON.parse(line);
                if (parsed.error) {
                    const { ApiError } = await import('./errors');
                    throw new ApiError(parsed.error.message ?? 'stream error', 200, parsed.error.code);
                }
                if (parsed.result !== undefined)
                    yield parsed.result;
            }
        }
        const tail = buffer.trim();
        if (tail) {
            const parsed = JSON.parse(tail);
            if (parsed.result !== undefined)
                yield parsed.result;
        }
    }
    finally {
        reader.releaseLock();
    }
}
//# sourceMappingURL=stream.js.map